/**
 * Authenticated Image Component
 *
 * Component to load images from endpoints that require Bearer token authentication.
 * Fetches the image with the auth token and displays it as a blob URL.
 */

import { useState, useEffect } from 'react'
import { Loader2, Image as ImageIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

interface AuthenticatedImageProps {
  /** Full URL to fetch the image from */
  src: string
  /** Alt text for the image */
  alt: string
  /** Function to get the auth token */
  getToken: () => string | null
  /** Optional className for styling */
  className?: string
  /** Optional click handler */
  onClick?: () => void
  /** Optional custom loading component */
  loadingComponent?: React.ReactNode
  /** Optional custom error component */
  errorComponent?: React.ReactNode
}

export function AuthenticatedImage({
  src,
  alt,
  getToken,
  className,
  onClick,
  loadingComponent,
  errorComponent,
}: AuthenticatedImageProps) {
  const [imageSrc, setImageSrc] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    let isMounted = true
    let objectUrl: string | null = null

    const fetchImage = async () => {
      try {
        const token = getToken()
        if (!token) {
          throw new Error('No auth token available')
        }

        const response = await fetch(src, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (!response.ok) throw new Error('Failed to load image')

        const blob = await response.blob()
        objectUrl = URL.createObjectURL(blob)

        if (isMounted) {
          setImageSrc(objectUrl)
          setLoading(false)
        }
      } catch (err) {
        console.error('Error loading authenticated image:', err)
        if (isMounted) {
          setError(true)
          setLoading(false)
        }
      }
    }

    setLoading(true)
    setError(false)
    setImageSrc(null)
    fetchImage()

    return () => {
      isMounted = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [src, getToken])

  if (loading) {
    return loadingComponent ? (
      <>{loadingComponent}</>
    ) : (
      <div className={cn(className, 'bg-[var(--secondary)] flex items-center justify-center')}>
        <Loader2 className="h-5 w-5 animate-spin text-[var(--foreground-muted)]" />
      </div>
    )
  }

  if (error || !imageSrc) {
    return errorComponent ? (
      <>{errorComponent}</>
    ) : (
      <div className={cn(className, 'bg-[var(--secondary)] flex items-center justify-center')}>
        <ImageIcon className="h-6 w-6 text-[var(--foreground-muted)]" />
      </div>
    )
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      onClick={onClick}
    />
  )
}
